import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Sparkles, Stethoscope, ShieldCheck, LayoutGrid, MessageSquare, BedDouble, BarChart3, CalendarDays, Package, FileText, Weight, FlaskConical, ClipboardList, Smartphone, BookOpen, Scale, Zap, ChevronDown, ChevronUp } from 'lucide-react';

const VERSIONS = [
  {
    version: '2.4',
    date: '20 Mart 2026',
    tag: 'Yeni',
    tagColor: 'bg-emerald-500',
    icon: ShieldCheck,
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-50',
    title: 'Yetki Sistemi & UI Standartları',
    items: [
      'Rol tabanlı yetkilendirme sistemi: Sahip, Veteriner, Teknisyen şablonları',
      '8 modül için okuma/yazma yetkileri tanımlanabilir',
      'Yetkisiz kullanıcılar için menü ve butonlar otomatik gizlenir',
      'Tüm modaller (~35 adet) standart boyuta getirildi',
      'Hasta kartları yeniden tasarlandı: kompakt, modern ve bilgi odaklı',
      'Gizlilik Politikası ve KVKK Aydınlatma Metni sayfaları eklendi',
    ],
  },
  {
    version: '2.3',
    date: '8 Şubat 2026',
    tag: null,
    icon: FileText,
    iconColor: 'text-violet-600',
    iconBg: 'bg-violet-50',
    title: 'Dijital Hasta Dosyası',
    items: [
      'Hasta not sistemi: Metin notları + dosya yükleme (görsel/PDF)',
      'Laboratuvar sonuçları modülü: Tür seçimi, dosya ekleme, filtreleme',
      'Kilo takip sistemi: Grafik, trend takibi, otomatik güncelleme',
      'Manuel SMS ve WhatsApp bildirim gönderme',
      'Kapsamlı Rehber modalı (30+ fonksiyon kılavuzu)',
      'Mobil navigasyon 4+1 yeniden tasarımı',
      'Landing page yeni pazarlama bölümleri',
    ],
  },
  {
    version: '2.2',
    date: '15 Aralık 2025',
    tag: null,
    icon: MessageSquare,
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-50',
    title: 'SMS Otomasyon & Bildirimler',
    items: [
      'NetGSM entegrasyonu ile otomatik SMS gönderimi',
      'SMS şablonları: Aşı hatırlatma, tedavi, randevu onay',
      'Zamanlanmış SMS görevleri (scheduler)',
      'SMS geçmişi ve kredi takibi',
      'Olay bazlı tetikleyiciler (işlem sonrası otomatik SMS)',
    ],
  },
  {
    version: '2.1',
    date: '3 Kasım 2025',
    tag: null,
    icon: BedDouble,
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-50',
    title: 'Konaklama (Boarding) Modülü',
    items: [
      'Oda/kafes yönetimi: Tür, kapasite, saatlik ücretlendirme',
      'Check-in / Check-out sistemi',
      'Dolu oda silme koruması',
      'Konaklama aktivite logları',
      'Ana sayfa özet kartında konaklama bilgisi',
    ],
  },
  {
    version: '2.0',
    date: '18 Eylül 2025',
    tag: null,
    icon: BarChart3,
    iconColor: 'text-rose-600',
    iconBg: 'bg-rose-50',
    title: 'Raporlama & Loglama',
    items: [
      'Kapsamlı raporlama: Ciro, Hasta, Operasyonel, Stok, Artış',
      'Birleşik log sistemi: Tüm aktiviteler tek merkezde',
      'Tarih bazlı filtreleme ve karşılaştırma',
    ],
  },
  {
    version: '1.5',
    date: '25 Ağustos 2025',
    tag: null,
    icon: CalendarDays,
    iconColor: 'text-indigo-600',
    iconBg: 'bg-indigo-50',
    title: 'Randevu & Takvim',
    items: [
      'Online randevu portalı: Müşteriler kendi randevusunu oluşturabilir',
      'Takvim görünümü: Günlük/haftalık planlama',
      'Randevu durum yönetimi: Onay, iptal, tamamlandı',
      'Randevu talepleri onay paneli',
    ],
  },
  {
    version: '1.0',
    date: '10 Temmuz 2025',
    tag: null,
    icon: Stethoscope,
    iconColor: 'text-gray-600',
    iconBg: 'bg-gray-100',
    title: 'Temel Klinik Sistemi',
    items: [
      'Müşteri ve hasta (pet) kayıt sistemi',
      'Aşı, tedavi ve hizmet işlemleri',
      'Stok yönetimi: Ürün takibi, barkod, son kullanım tarihi',
      'Kasa ve muhasebe: Nakit, kart, havale takibi',
      'Çoklu klinik desteği',
      'Kimlik doğrulama ve kullanıcı rolleri',
    ],
  },
];

export default function UpdateHistoryModal({ open, onClose }) {
  const [expandedIdx, setExpandedIdx] = useState(0);

  const toggle = (idx) => {
    setExpandedIdx(expandedIdx === idx ? -1 : idx);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="update-history-modal">
        <DialogHeader className="px-4 pt-4 sm:px-0 sm:pt-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={20} className="text-[#6b6de3]" />
            Güncelleme Geçmişi
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500 mt-1">Tüm sürümler ve yenilikler</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4 sm:px-0 sm:pb-0 space-y-2 mt-3" data-testid="update-history-list">
          {VERSIONS.map((v, idx) => {
            const Icon = v.icon;
            const isOpen = expandedIdx === idx;
            return (
              <div
                key={v.version}
                className={`rounded-xl border transition-all duration-200 ${isOpen ? 'border-[#6b6de3]/30 bg-white shadow-sm' : 'border-gray-100 bg-gray-50/50 hover:bg-white'}`}
                data-testid={`version-card-${v.version}`}
              >
                <button
                  onClick={() => toggle(idx)}
                  className="w-full flex items-center gap-3 p-3 text-left"
                  data-testid={`version-toggle-${v.version}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${v.iconBg}`}>
                    <Icon size={18} className={v.iconColor} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-900">v{v.version}</span>
                      {v.tag && (
                        <span className={`text-[10px] font-bold text-white px-1.5 py-0.5 rounded-full ${v.tagColor}`}>
                          {v.tag}
                        </span>
                      )}
                      <span className="text-[11px] text-gray-400 ml-auto flex-shrink-0">{v.date}</span>
                    </div>
                    <p className="text-xs text-gray-600 font-medium truncate">{v.title}</p>
                  </div>
                  {isOpen ? (
                    <ChevronUp size={16} className="text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
                  )}
                </button>

                {isOpen && (
                  <div className="px-3 pb-3 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="ml-12 space-y-1.5">
                      {v.items.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#6b6de3] mt-1.5 flex-shrink-0" />
                          <span className="text-xs text-gray-700 leading-relaxed">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
